import isEmpty from 'lodash/isEmpty';

import PixelService from '@/services/pixel';

import {
  buildLocationHostUrl,
  convertObjectKeys,
  filterObject,
  getApp,
  parseSearchParams,
} from '@/helpers/core';
import MainStorage from '@/storage/main';
import { setGtmMarketing } from '@/helpers/gtm';

import {
  MARKETING_PARAMS_MAPPING,
  MARKETING_SEARCH_PARAMS,
  MarketingParam,
} from '@/config/analytics';

function parse(): Dictionary<string | number> {
  return filterObject(parseSearchParams(window.location.search), MARKETING_SEARCH_PARAMS);
}

function buildLandingParams(params: Dictionary<string | number>): Dictionary<string | number> {
  const result = { ...params };

  if (!isEmpty(result)) {
    result[MarketingParam.LANDING_URL] = buildLocationHostUrl();
  }

  return convertObjectKeys(result, MARKETING_PARAMS_MAPPING);
}

function buildPixelParams(): Dictionary<string | null> {
  const { fbc, fbp } = PixelService.getValues();

  return {
    [MarketingParam.FBC]: fbc,
    [MarketingParam.FBP]: fbp,
  };
}

async function fetchTime(): Promise<number> {
  let response;
  try {
    response = await fetch('/time', { method: 'HEAD', cache: 'no-store' });
  } catch {
    return 0;
  }

  const dateHdr = response.headers.get('X-Current-Time');
  if (dateHdr == null) {
    return 0;
  }
  return Math.trunc(Number(dateHdr));
}

const MARKETING_PARAMS: Dictionary<string | number> = {};

function getParamsPriority(params: Dictionary<string | number>): number {
  const mediaSource = params.media_source?.toString();
  if (!mediaSource) return 0; // low priority
  return mediaSource === 'organic' ? 1 : 2; // medium priority for organic, high for others
}

async function initMarketingParams() {
  const app = getApp();
  if (app === null) {
    return;
  }

  const parsedParams = {};
  const searchParams = parse();
  if (Object.keys(searchParams).length > 0) {
    PixelService.initCookies(searchParams.fbclid?.toString());
    Object.assign(parsedParams, { timestamp: await fetchTime() });

    // Handle wbraid and gbraid as fallbacks for empty gclid when utm_source is googleadwords_int
    if (!searchParams.gclid) {
      if (searchParams.wbraid) {
        searchParams.gclid = searchParams.wbraid;
        delete searchParams.wbraid;
      } else if (searchParams.gbraid) {
        searchParams.gclid = searchParams.gbraid;
        delete searchParams.gbraid;
      }
    }

    Object.assign(parsedParams, buildLandingParams(searchParams));
    Object.assign(parsedParams, buildPixelParams());
  }

  const savedParams = MainStorage.getMarketingParams(app);

  const parsedPriority = getParamsPriority(parsedParams);
  const savedPriority = getParamsPriority(savedParams);

  // Use parsed params if they have higher or equal priority
  if (parsedPriority >= savedPriority && Object.keys(parsedParams).length > 0) {
    MainStorage.saveMarketingParams(app, parsedParams);
    setGtmMarketing(parsedParams);
    Object.assign(MARKETING_PARAMS, parsedParams);
    return;
  }

  // Use saved params if they have higher priority or no parsed params exist
  if (savedPriority > parsedPriority || Object.keys(parsedParams).length === 0) {
    setGtmMarketing(savedParams);
    Object.assign(MARKETING_PARAMS, savedParams);
  }
}

export { initMarketingParams, MARKETING_PARAMS };
